"use client";

import { useStore } from '@/lib/state/store';
import { useEffect } from 'react';
import { auth } from '@/lib/firebase/client';
import { onAuthStateChanged } from 'firebase/auth';
import { usePresence } from '@/hooks/use-presence';
import {
  PRIMARY_USER_EMAIL,
  PRIMARY_USER_NAME,
  SECONDARY_USER_EMAIL,
  SECONDARY_USER_NAME,
  HEATHER_USER_EMAIL,
  HEATHER_USER_NAME,
} from '@/lib/user-config';

export function StoreInitializer() {
  const setCurrentUser = useStore(state => state.setCurrentUser);
  const currentUser = useStore(state => state.currentUser);
  const setDeviceId = useStore(state => state.setDeviceId);

  // Initialize team presence heartbeats
  usePresence();

  useEffect(() => {
    // 1. Auth Sync: Prioritize Firebase Auth state; only fallback to localStorage if strictly offline
    let unsubscribe = () => {};

    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const isExplicitBypass = typeof window !== 'undefined' && window.localStorage.getItem('bypassAuth') === 'true';

    if (isOffline && isExplicitBypass) {
      const cachedUser = typeof window !== 'undefined' ? window.localStorage.getItem('currentUser') || PRIMARY_USER_NAME : PRIMARY_USER_NAME;
      if (process.env.NODE_ENV === 'development') console.log(`[StoreInitializer] Offline Bypass Mode Active: ${cachedUser}`);
      useStore.getState().setAuthInitializing(false);
      setCurrentUser(cachedUser);
    } else if (auth) {
      try {
        unsubscribe = onAuthStateChanged(auth, (user) => {
          // Once this fires, the online auth state is definitive
          useStore.getState().setAuthInitializing(false);

          if (user) {
            // Map common emails to short names used in the app
            const email = user.email?.toLowerCase();
            let shortName = user.displayName?.split(' ')[0] || 'User';

            if (email === PRIMARY_USER_EMAIL || shortName.toLowerCase() === PRIMARY_USER_NAME.toLowerCase()) {
              shortName = PRIMARY_USER_NAME;
            } else if (email === SECONDARY_USER_EMAIL || shortName.toLowerCase() === SECONDARY_USER_NAME.toLowerCase()) {
              shortName = SECONDARY_USER_NAME;
            } else if (email === HEATHER_USER_EMAIL || shortName.toLowerCase() === HEATHER_USER_NAME.toLowerCase()) {
              shortName = HEATHER_USER_NAME;
            }

            if (process.env.NODE_ENV === 'development') console.log(`[StoreInitializer] Identity Sync: ${email} -> ${shortName}`);
            setCurrentUser(shortName);
            window.localStorage.setItem('currentUser', shortName);
          } else {
            // If strictly offline, retain cached user identity for field continuity
            const currentlyOffline = typeof navigator !== 'undefined' && !navigator.onLine;
            const cachedUser = typeof window !== 'undefined' ? window.localStorage.getItem('currentUser') : null;

            if (currentlyOffline && cachedUser) {
              if (process.env.NODE_ENV === 'development') console.log(`[StoreInitializer] Offline fallback: Preserving ${cachedUser}`);
              setCurrentUser(cachedUser);
            } else if (isExplicitBypass && cachedUser) {
              setCurrentUser(cachedUser);
            } else if (process.env.NODE_ENV === 'development' || !auth) {
              // Local development & field offline operations preserve cachedUser or PRIMARY_USER_NAME
              const devUser = cachedUser || PRIMARY_USER_NAME;
              if (process.env.NODE_ENV === 'development') console.log(`[StoreInitializer] Development fallback: ${devUser}`);
              setCurrentUser(devUser);
              if (typeof window !== 'undefined') {
                window.localStorage.setItem('currentUser', devUser);
              }
            } else {
              setCurrentUser(null);
              if (typeof window !== 'undefined') {
                window.localStorage.removeItem('currentUser');
              }
            }
          }
        });
      } catch (err) {
        console.warn("[StoreInitializer] Firebase Auth down, falling back to offline mode:", err);
        useStore.getState().setAuthInitializing(false);
        const cachedUser = typeof window !== 'undefined' ? window.localStorage.getItem('currentUser') || PRIMARY_USER_NAME : PRIMARY_USER_NAME;
        setCurrentUser(cachedUser);
      }
    } else {
      useStore.getState().setAuthInitializing(false);
      const cachedUser = typeof window !== 'undefined' ? window.localStorage.getItem('currentUser') || PRIMARY_USER_NAME : PRIMARY_USER_NAME;
      setCurrentUser(cachedUser);
    }

    // 2. Device ID setup (client-side only)
    try {
      if (typeof window !== 'undefined') {
        const storedId = window.localStorage.getItem('device_id');
        if (storedId) {
          setDeviceId(storedId);
        } else {
          const uuid = typeof crypto !== 'undefined' && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : Math.random().toString(36).slice(2);
          const newId = `device_${uuid}`;
          window.localStorage.setItem('device_id', newId);
          setDeviceId(newId);
        }
      }
    } catch { }

    return () => unsubscribe();
  }, [setCurrentUser, setDeviceId]);

  return null;
}
